// client/src/services/api.js - Vereinfachte und robustere Implementation

// Base URL from environment variables
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Helper function to get auth header with token
 */
const getAuthHeader = () => {
    try {
        const token = localStorage.getItem('token');
        if (token) {
            console.log('Token gefunden');
            return { 'Authorization': `Bearer ${token}` };
        }
    } catch (error) {
        console.error('Fehler beim Abrufen des Tokens:', error);
    }
    return {};
};

/**
 * Helper to handle fetch responses
 */
const handleResponse = async (response) => {
    try {
        // Parse response as JSON
        const data = await response.json();

        // If the response is not ok, format the error
        if (!response.ok) {
            console.error('API-Fehler:', data);
            return {
                success: false,
                status: response.status,
                message: data.message || data.error || `Fehler: ${response.status} ${response.statusText}`,
                data: null
            };
        }

        // Standardmäßig success auf true setzen, wenn es nicht definiert ist
        if (typeof data.success === 'undefined') {
            data.success = true;
        }

        return data;
    } catch (error) {
        console.error('Fehler beim Verarbeiten der API-Antwort:', error);
        return {
            success: false,
            message: 'Fehler beim Verarbeiten der Serverantwort',
            error: error.message
        };
    }
};

/**
 * API Service mit standardisierten Methoden
 */
const api = {
    // GET-Anfrage
    get: async (endpoint, params = {}) => {
        try {
            // Build URL with query parameters
            const url = new URL(`${API_URL}${endpoint}`);

            // Parameter zur URL hinzufügen
            Object.keys(params).forEach(key => {
                if (params[key] !== null && params[key] !== undefined) {
                    url.searchParams.append(key, params[key]);
                }
            });

            // Authentifizierungs-Header
            const headers = {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            };

            // Anfrage senden
            const response = await fetch(url.toString(), {
                method: 'GET',
                headers
            });

            return handleResponse(response);
        } catch (error) {
            console.error('API GET-Fehler:', error);
            return {
                success: false,
                message: error.message || 'Fehler beim Abrufen der Daten'
            };
        }
    },

    // POST-Anfrage
    post: async (endpoint, data = {}, token = null) => {
        try {
            // Authentifizierungs-Header
            const headers = {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : getAuthHeader())
            };

            // Anfrage senden
            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data)
            });

            return handleResponse(response);
        } catch (error) {
            console.error('API POST-Fehler:', error);
            return {
                success: false,
                message: error.message || 'Fehler beim Senden der Daten'
            };
        }
    },

    // PUT-Anfrage
    put: async (endpoint, data = {}) => {
        try {
            // Authentifizierungs-Header
            const headers = {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            };

            // Anfrage senden
            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(data)
            });

            return handleResponse(response);
        } catch (error) {
            console.error('API PUT-Fehler:', error);
            return {
                success: false,
                message: error.message || 'Fehler beim Aktualisieren der Daten'
            };
        }
    },

    // DELETE-Anfrage
    delete: async (endpoint) => {
        try {
            // Authentifizierungs-Header
            const headers = {
                'Content-Type': 'application/json',
                ...getAuthHeader()
            };

            // Anfrage senden
            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'DELETE',
                headers
            });

            return handleResponse(response);
        } catch (error) {
            console.error('API DELETE-Fehler:', error);
            return {
                success: false,
                message: error.message || 'Fehler beim Löschen der Daten'
            };
        }
    },

    // Upload-Anfrage (für FormData)
    upload: async (endpoint, formData, token = null) => {
        try {
            // Nur Authentifizierungs-Header (Content-Type wird vom Browser für FormData gesetzt)
            const headers = {
                ...(token ? { 'Authorization': `Bearer ${token}` } : getAuthHeader())
            };

            // Anfrage senden
            const response = await fetch(`${API_URL}${endpoint}`, {
                method: 'POST', // Oder PUT, je nach Anwendungsfall, aber POST für neue Shops
                headers,
                body: formData // FormData direkt als Body
            });

            return handleResponse(response);
        } catch (error) {
            console.error('API Upload-Fehler:', error);
            return {
                success: false,
                message: error.message || 'Fehler beim Hochladen der Daten'
            };
        }
    }
};

export default api;